import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { QUERY_KEYS } from '@/lib/queryKeys'

const IMPERSONATION_STORAGE_KEY = 'impersonate-user-id'

const listeners = new Set<() => void>()

const notifyImpersonationChange = () => {
  listeners.forEach(listener => listener())
}

export const getImpersonatedUserId = (): string | null => {
  return sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)
}

export const setImpersonatedUserId = (userId: string) => {
  sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, userId)
  notifyImpersonationChange()
}

export const clearImpersonation = () => {
  sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
  notifyImpersonationChange()
}

/** Office routes all start with `/office` (including `/office-userPage`). */
export const isOfficePathname = (pathname: string): boolean => {
  return pathname.startsWith('/office')
}

const IMPERSONATED_CLIENT_QUERY_KEYS = [QUERY_KEYS.USER_PACKAGES, QUERY_KEYS.USER_PICKUP_ORDERS, QUERY_KEYS.ADDRESSES] as const

/** Drop the client session created by "View as client" and refetch the real staff user. */
export const releaseImpersonation = async (queryClient: QueryClient) => {
  clearImpersonation()
  for (const key of IMPERSONATED_CLIENT_QUERY_KEYS) {
    queryClient.removeQueries({ queryKey: [key] })
  }
  await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LOGGED_USER] })
}

export const useImpersonatedUserId = () => {
  const [impersonatedUserId, setImpersonatedUserIdState] = useState<string | null>(() => getImpersonatedUserId())

  useEffect(() => {
    const listener = () => setImpersonatedUserIdState(getImpersonatedUserId())
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return impersonatedUserId
}

/**
 * End impersonation when navigation lands on an office page.
 * Tracks pathname changes only, so "View as client" can store the target
 * user while the office user page is still open and then leave for the client area.
 */
export const useClearImpersonationOnOfficePage = (pathname: string) => {
  const queryClient = useQueryClient()
  const previousPathRef = useRef<string | null>(null)
  const pendingReleaseRef = useRef(false)

  if (previousPathRef.current !== pathname) {
    previousPathRef.current = pathname
    // Drop the stored id before child pages fetch. Notifying listeners here would
    // set React state during render; the effect below finishes that update.
    if (isOfficePathname(pathname) && getImpersonatedUserId()) {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      pendingReleaseRef.current = true
    }
  }

  useEffect(() => {
    if (!pendingReleaseRef.current) return
    pendingReleaseRef.current = false
    void releaseImpersonation(queryClient)
  }, [pathname, queryClient])
}

const IMPERSONATION_SCOPED_PATH_PREFIXES = [
  '/users/me',
  '/user-pkg',
  '/user-pickup-orders',
  '/addresses',
  '/payments/',
  '/coupons/check',
  '/preqs',
] as const

export const shouldAttachImpersonationHeader = (url?: string): boolean => {
  if (!url) {
    return false
  }

  const path = url.startsWith('http') ? new URL(url).pathname : url
  return IMPERSONATION_SCOPED_PATH_PREFIXES.some(prefix => path.includes(prefix))
}

export const IMPERSONATE_USER_ID_HEADER = 'X-Impersonate-User-Id'
